/**
 * Payment Method picker — radio cards for the Secure Checkout.
 *
 * Renders one card per available provider from
 * `GET /api/shop/payments/providers`.  Providers whose credentials
 * aren't configured yet render as disabled with a "Coming soon" tag.
 *
 * Layout matches the existing KR checkout aesthetic — light card with
 * a colored ring on the selected option, no third-party UI kit.
 */
import { CreditCard, CheckCircle2 } from 'lucide-react';
import { Label } from '@/components/ui/label';

const LOGOS = {
  quickbooks: null, // rendered as a CreditCard icon (native)
  paypal: 'https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_37x23.jpg',
  // Afterpay's official CDN blocks hotlinking (403).  Show the wordmark
  // as bold text until the merchant onboarding provides an image asset
  // we can self-host.
  afterpay: null,
  klarna: 'https://x.klarnacdn.net/payment-method/assets/badges/generic/klarna.svg',
};

const METHOD_LABELS = {
  card: 'Credit Card',
  paypal: 'PayPal',
  paypal_paylater: 'PayPal Pay Later',
  afterpay: 'Afterpay',
  klarna: 'Klarna',
};

const METHOD_HINT = {
  card: 'Visa, Mastercard, Amex, Discover',
  paypal: 'Log in and pay with your PayPal balance, bank, or card',
  paypal_paylater: 'Split into 4 payments — eligibility checked by PayPal',
  afterpay: 'Pay in 4 interest-free installments',
  klarna: 'Pay now, in 4, or in 30 days — chosen at Klarna',
};

/**
 * Expand `providers` into individual selectable rows.  A provider that
 * declares multiple methods (PayPal → paypal + paypal_paylater) renders
 * as two rows so the customer can pick Pay Later explicitly.
 */
function flattenProviderMethods(providers) {
  const rows = [];
  for (const p of providers) {
    for (const m of p.methods || []) {
      rows.push({
        method: m,
        providerId: p.id,
        providerLabel: p.label,
        enabled: !!p.enabled,
        publicConfig: p.public_config || {},
      });
    }
  }
  return rows;
}

export default function PaymentMethodPicker({
  providers,
  value,       // selected method string
  onChange,    // (method) => void
  paypalEligibility, // { paylater: bool, venmo: bool } — computed from SDK
}) {
  const rows = flattenProviderMethods(providers || []);

  return (
    <div className="space-y-2" data-testid="payment-method-picker">
      <Label className="text-xs uppercase tracking-wide text-slate-500">Payment method</Label>
      {rows.map((row) => {
        const selected = value === row.method;
        // Hide Pay Later if PayPal SDK reports ineligibility.  Standard
        // PayPal always shows when the provider is enabled.
        if (row.method === 'paypal_paylater' && paypalEligibility && !paypalEligibility.paylater) {
          return null;
        }
        const disabled = !row.enabled;
        return (
          <button
            type="button"
            key={row.method}
            disabled={disabled}
            onClick={() => !disabled && onChange(row.method)}
            data-testid={`pm-option-${row.method}`}
            className={[
              'w-full text-left border rounded-lg p-3 transition-all flex items-center gap-3',
              selected ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-400',
              disabled ? 'opacity-50 cursor-not-allowed hover:border-slate-200' : 'cursor-pointer',
            ].join(' ')}
          >
            <span
              className={[
                'inline-flex items-center justify-center h-5 w-5 rounded-full border-2 shrink-0',
                selected ? 'border-slate-900' : 'border-slate-300',
              ].join(' ')}
            >
              {selected && <span className="h-2.5 w-2.5 rounded-full bg-slate-900" />}
            </span>

            <span className="flex-1 min-w-0">
              <span className="flex items-center gap-2 font-medium text-slate-900 text-sm">
                {row.providerId === 'quickbooks' && <CreditCard size={16} className="text-slate-500" />}
                {LOGOS[row.providerId] && row.providerId !== 'quickbooks' && (
                  <img src={LOGOS[row.providerId]} alt={row.providerLabel}
                       className="h-4 w-auto max-w-[70px] object-contain" />
                )}
                <span>{METHOD_LABELS[row.method] || row.providerLabel}</span>
                {disabled && (
                  <span className="ml-auto text-[10px] uppercase tracking-wide bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                    Coming soon
                  </span>
                )}
              </span>
              <span className="block text-xs text-slate-500 mt-0.5">{METHOD_HINT[row.method]}</span>
            </span>

            {selected && <CheckCircle2 size={16} className="text-slate-900 shrink-0" />}
          </button>
        );
      })}
    </div>
  );
}
